import {
  CloseIcon,
  JoonUIColor,
  PaperclipIcon,
  PaperPlaneIcon,
  SPACING,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useEffect } from "react"

import {
  AIMessageActionBar,
  AIMessageSuggestionBox,
} from "./AIMessageGenerator"
import { useSendbird } from "./SBProvider"
import useAIMessageStore from "./useAIMessageStore"
import { useSBStore } from "./useSBStore"
import { useThrottle } from "../../hooks/useThrottle"
import useUserRole from "../../hooks/useUserRole"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import AttachmentItem from "../attachments/AttachmentItem"
import NextScheduledMessage from "../nextScheduledMessage/NextScheduledMessage"
import CustomTextArea from "../textarea/TextArea"

function CustomizedMessageInput() {
  const {
    messageToReplyTo,
    setMessageToReplyTo,
    messageToEdit,
    setMessageToEdit,
    inputText,
    setInputText,
    attachments,
    setAttachments,
  } = useSBStore()
  const {
    sendMessage,
    sendFileMessage,
    currentChannel,
    updateMessage,
    checkIfFileSizeIsTooLarge,
  } = useSendbird()
  const { hasCopiedSuggestion, setHasCopiedSuggestion } = useAIMessageStore()
  const { isAdmin, isClientOfOtherCoach } = useUserRole()

  const isInputEmpty = inputText.length < 1 && attachments.length === 0

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isAdmin || isClientOfOtherCoach) return
    if (!event.target.files || !event.target.files[0]) return

    setAttachments([...attachments, ...event.target.files])
    // Reset the input value so the same file can be selected again
    event.target.value = ""
  }

  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments]
    newAttachments.splice(index, 1)
    setAttachments(newAttachments)
  }

  const sendUserMessage = useThrottle(async (event: any) => {
    if (isAdmin || isClientOfOtherCoach) return
    event.preventDefault()

    if (isInputEmpty) return
    if (!currentChannel) return

    if (messageToEdit) {
      await updateMessage(
        currentChannel.url,
        messageToEdit.messageId,
        inputText
      )
      setMessageToEdit(null)
    } else {
      // Check if any file is too large
      for (const file of attachments) {
        if (checkIfFileSizeIsTooLarge(file)) return
      }

      // Send text message if there's any
      if (inputText.trim()) {
        await sendMessage(
          currentChannel.url,
          inputText,
          messageToReplyTo?.messageId
        )
      }

      // Send all staged files
      for (const file of attachments) {
        await sendFileMessage(currentChannel.url, file)
      }
    }

    if (hasCopiedSuggestion) {
      trackAnalyticEvent(ANALYTIC_EVENTS.AI_SEND)
    }

    setInputText("")
    setMessageToReplyTo(null)
    setAttachments([])
  }, 500)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      sendUserMessage(event)
    }
  }

  useEffect(() => {
    if (inputText.length === 0) {
      setHasCopiedSuggestion(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText])

  useEffect(() => {
    if (messageToEdit) setInputText(messageToEdit.message)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageToEdit])

  return (
    <form onSubmit={sendUserMessage} className="p-6 pb-4 pt-2 w-full">
      {(messageToReplyTo || messageToEdit) && (
        <div className="flex flex-row justify-between mb-2">
          <div>
            <Typography variant="bodySmallBold">
              {messageToReplyTo ? "Reply to:" : "Editing message:"}
            </Typography>
            <Typography
              variant="bodyXSmall"
              color={JoonUIColor.text.secondary}
              textAlign="left"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {messageToReplyTo?.message || messageToEdit?.message}
            </Typography>
          </div>
          <TextButton
            onClick={() => {
              if (messageToEdit) {
                setInputText("")
                setMessageToEdit(null)
              } else {
                setMessageToReplyTo(null)
              }
            }}
          >
            <CloseIcon size={20} color={JoonUIColor.icon.neutral} />
          </TextButton>
        </div>
      )}
      <AIMessageActionBar />
      <div className="flex flex-col border border-borderPrimary rounded-md box-shadow-low overflow-hidden">
        <AIMessageSuggestionBox />

        <div className="flex flex-row items-center rounded-tl-none rounded-tr-none pt-0.5 pr-4 pb-0 pl-2">
          <div className="flex items-center justify-center ml-auto h-8 w-8">
            {/* eslint-disable-next-line */}
            <label htmlFor="icon-button-file" className="cursor-pointer flex">
              <PaperclipIcon size={22} color={JoonUIColor.text.primary} />
              <input
                accept="image/*,video/*,application/pdf"
                type="file"
                onChange={handleFileSelect}
                id="icon-button-file"
                style={{ display: "none" }}
                name="icon-button-file"
              />
            </label>
          </div>
          <CustomTextArea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={5000}
            placeholder="Type a message..."
            disabled={isAdmin || isClientOfOtherCoach}
            defaultRows={1}
            style={{
              maxHeight: "200px",
              fontSize: "16px",
              overflow: "auto",
              border: "none",
              outline: "none",
              padding: `${SPACING.space2} ${SPACING.space1}`,
            }}
            fullWidth
          />
          <div className="flex items-center justify-center ml-auto h-8 w-8">
            {!isInputEmpty && (
              <button style={{ padding: 0 }} type="submit">
                <PaperPlaneIcon
                  color={
                    isInputEmpty
                      ? JoonUIColor.icon.neutral
                      : JoonUIColor.icon.accent
                  }
                  size={22}
                />
              </button>
            )}
          </div>
        </div>
        {attachments.length > 0 && (
          <div className="flex flex-col gap-1 pb-2 px-3">
            {attachments.map((file, index) => (
              <AttachmentItem
                name={file.name}
                url={URL.createObjectURL(file)}
                key={file.name}
                onRemove={() => removeAttachment(index)}
                className="max-w-[400px]"
              />
            ))}
          </div>
        )}
      </div>
      <NextScheduledMessage />
    </form>
  )
}

export default CustomizedMessageInput
